import React, { useEffect, useMemo, useState } from 'react';
import { X, Search } from 'lucide-react';
import api from '../../../services/api';
import { bodyVariables } from './chatUtils';

const fill = (text = '', values = {}) =>
  text.replace(/\{\{\s*(\w+)\s*\}\}/g, (m, key) => (values[key] ? values[key] : m));

// Why a template can't be sent from here — media headers and URL-variable
// buttons need inputs this compact picker doesn't collect.
function unsupportedReason(template) {
  const header = template.components?.find((c) => c.type === 'HEADER');
  if (header && header.format && header.format !== 'TEXT') return 'Has a media header — send it from Direct Message.';
  const named = [...bodyVariables(header?.text), ...bodyVariables(template.components?.find((c) => c.type === 'BODY')?.text)].some((k) => !/^\d+$/.test(k));
  if (named) return 'Uses named variables — send it from Direct Message.';
  const buttons = template.components?.find((c) => c.type === 'BUTTONS')?.buttons || [];
  if (buttons.some((b) => b.type === 'OTP' || (b.type === 'URL' && /\{\{/.test(b.url || '')))) return 'Has a dynamic button — send it from Direct Message.';
  return null;
}

export default function TemplatePickerModal({ open, onClose, onSend, sending, contactName }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState(null);
  const [values, setValues] = useState({});

  useEffect(() => {
    if (!open) return undefined;
    setSelected(null); setValues({}); setQuery(''); setLoadError(null);
    let cancelled = false;
    setLoading(true);
    api.get('/meta/templates')
      .then((res) => { if (!cancelled) setTemplates((res.data.templates || []).filter((t) => t.status === 'APPROVED')); })
      .catch(() => { if (!cancelled) setLoadError('Could not load templates.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open]);

  const filtered = useMemo(
    () => templates.filter((t) => !query || t.name.toLowerCase().includes(query.toLowerCase())),
    [templates, query]
  );

  const comp = (type) => selected?.components?.find((c) => c.type === type);
  const headerVars = selected ? bodyVariables(comp('HEADER')?.format === 'TEXT' ? comp('HEADER')?.text : '') : [];
  const bodyVars = selected ? bodyVariables(comp('BODY')?.text) : [];
  const blocked = selected ? unsupportedReason(selected) : null;
  const missing = [...headerVars.map((k) => `h${k}`), ...bodyVars.map((k) => `b${k}`)].some((k) => !values[k]?.trim());

  if (!open) return null;

  const submit = () => {
    const components = [];
    if (headerVars.length) {
      components.push({ type: 'header', parameters: headerVars.map((k) => ({ type: 'text', text: values[`h${k}`] })) });
    }
    if (bodyVars.length) {
      components.push({ type: 'body', parameters: bodyVars.map((k) => ({ type: 'text', text: values[`b${k}`] })) });
    }
    onSend({ templateName: selected.name, language: selected.language, components });
  };

  const bodyValues = Object.fromEntries(bodyVars.map((k) => [k, values[`b${k}`]]));
  const headerValues = Object.fromEntries(headerVars.map((k) => [k, values[`h${k}`]]));

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 p-4" role="dialog" aria-modal="true" aria-label="Send a template">
      <div className="bg-white rounded-lg shadow-[0_2px_8px_rgba(0,0,0,0.16)] w-full max-w-2xl max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-3 border-b border-[#E5E5E5]">
          <h2 className="text-base font-semibold text-[#2E2E2E]">Send a template{contactName ? ` to ${contactName}` : ''}</h2>
          <button onClick={onClose} aria-label="Close" className="text-[#706E6B] hover:text-[#5C5C5C]"><X size={18} /></button>
        </div>

        <div className="flex flex-1 min-h-0 flex-col md:flex-row">
          <div className="md:w-60 md:border-r border-[#E5E5E5] flex flex-col max-h-48 md:max-h-none">
            <div className="p-3 relative">
              <Search className="absolute left-5 top-1/2 -translate-y-1/2 text-[#706E6B]" size={14} />
              <input
                value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search templates…"
                className="w-full pl-8 pr-2 py-1.5 text-sm border border-[#E5E5E5] rounded focus:outline-none focus:ring-2 focus:ring-[#25D366]"
              />
            </div>
            <div className="flex-1 overflow-y-auto">
              {loading && <p className="text-xs text-[#706E6B] p-3">Loading…</p>}
              {loadError && <p className="text-xs text-[#BA0517] p-3">{loadError}</p>}
              {!loading && !loadError && filtered.length === 0 && (
                <p className="text-xs text-[#706E6B] p-3">No approved templates found.</p>
              )}
              {filtered.map((t) => (
                <button
                  key={t._id}
                  onClick={() => { setSelected(t); setValues({}); }}
                  className={`w-full text-left px-3 py-2 border-b border-[#F3F2F2] hover:bg-[#F7F9FB] ${selected?._id === t._id ? 'bg-[#E3F3E8]' : ''}`}
                >
                  <p className="text-sm font-medium text-[#2E2E2E] truncate">{t.name}</p>
                  <p className="text-[11px] text-[#706E6B]">{t.language} · {t.category}</p>
                </button>
              ))}
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-4">
            {!selected && <p className="text-sm text-[#706E6B]">Choose a template to preview it.</p>}
            {selected && (
              <>
                {[...headerVars.map((k) => ['h', k, 'Header']), ...bodyVars.map((k) => ['b', k, 'Body'])].map(([p, k, label]) => (
                  <label key={`${p}${k}`} className="block mb-2">
                    <span className="text-xs text-[#5C5C5C]">{label} variable {`{{${k}}}`}</span>
                    <input
                      value={values[`${p}${k}`] || ''}
                      onChange={(e) => setValues((v) => ({ ...v, [`${p}${k}`]: e.target.value }))}
                      className="mt-0.5 w-full px-2 py-1.5 text-sm border border-[#E5E5E5] rounded focus:outline-none focus:ring-2 focus:ring-[#25D366]"
                    />
                  </label>
                ))}
                <div className="mt-3 rounded-lg bg-[#D9FDD3] px-3 py-2 text-sm text-[#2E2E2E]">
                  {comp('HEADER')?.format === 'TEXT' && <p className="font-semibold mb-1">{fill(comp('HEADER')?.text, headerValues)}</p>}
                  <p className="whitespace-pre-wrap">{fill(comp('BODY')?.text, bodyValues)}</p>
                  {comp('FOOTER')?.text && <p className="text-xs text-[#706E6B] mt-1">{comp('FOOTER').text}</p>}
                </div>
                {blocked && <p className="mt-3 text-xs text-[#8C4B02] bg-[#FFF4E5] border border-[#FFB75D] rounded px-2 py-1.5">{blocked}</p>}
              </>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-2 px-5 py-3 border-t border-[#E5E5E5]">
          <button onClick={onClose} className="px-3 py-1.5 text-sm border border-[#D8DDE6] rounded hover:bg-[#F7F9FB]">Cancel</button>
          <button
            onClick={submit}
            disabled={!selected || !!blocked || missing || sending}
            className="px-3 py-1.5 text-sm bg-[#25D366] hover:bg-[#1FAF56] text-white rounded disabled:opacity-50"
          >
            {sending ? 'Sending…' : 'Send template'}
          </button>
        </div>
      </div>
    </div>
  );
}
